import { Box, Grid, Stack, Typography } from '@mui/material'
import { useState } from 'react'
import { FormSpy, useForm } from 'react-final-form'
import { DataPanel } from '../../../components/DataPanel'
import MultistepFormField, {
    singleSelectCheckboxUtil,
} from '../../../components/MultistepForm/MultistepFormField'
import { getSubscriberInformation } from './CoverageStep'

export const PATIENT_RELATIONSHIP_FIELDS = [
    'patientInfo-relationshipSelf',
    'patientInfo-relationshipSpouse',
    'patientInfo-relationshipDependentChild',
    'patientInfo-relationshipOther',
]

const PatientInformationStep = () => {
    const form = useForm()
    const [selfRelationshipEnabled, setSelfRelationshipEnabled] = useState(
        form.getState().values['patientInfo-relationshipSelf'] ?? false,
    )

    const onFormChange = () => {
        const selfRelationshipFieldValue =
            form.getState().values['patientInfo-relationshipSelf'] ?? false
        const lastSelfFieldValue = selfRelationshipEnabled
        setSelfRelationshipEnabled(selfRelationshipFieldValue)

        const { change } = form
        const subscriber = getSubscriberInformation(form)
        if (selfRelationshipFieldValue) {
            change('patientInfo-firstName', subscriber.firstName)
            change('patientInfo-lastName', subscriber.lastName)
            change('patientInfo-middleInitial', subscriber.middleInitial)
            change('patientInfo-suffix', subscriber.suffix)
            change('patientInfo-address', subscriber.address.addressLine1)
            change('patientInfo-state', subscriber.address.state)
            change('patientInfo-city', subscriber.address.city)
            change('patientInfo-zipCode', subscriber.address.zipCode)
            switch (subscriber.gender?.toUpperCase()) {
                case 'M':
                    change('patientInfo-genderMale', true)
                    change('patientInfo-genderFemale', false)
                    break
                case 'F':
                    change('patientInfo-genderFemale', true)
                    change('patientInfo-genderMale', false)
            }
        } else if (lastSelfFieldValue) {
            change('patientInfo-firstName', undefined)
            change('patientInfo-lastName', undefined)
            change('patientInfo-middleInitial', undefined)
            change('patientInfo-suffix', undefined)
            change('patientInfo-address', undefined)
            change('patientInfo-state', undefined)
            change('patientInfo-city', undefined)
            change('patientInfo-zipCode', undefined)
            change('patientInfo-genderMale', false)
            change('patientInfo-genderFemale', false)
            change('patientInfo-patientId', undefined)
        }
    }

    return (
        <Box>
            <FormSpy subscription={{ values: true }} onChange={onFormChange} />
            <DataPanel
                title="Patient Information"
                content={
                    <Grid container spacing={2}>
                        <Grid item xs={4}>
                            <Stack spacing={2}>
                                <Box>
                                    <Typography>Relationship to Primary Subscriber</Typography>
                                    <Stack>
                                        <MultistepFormField
                                            type="checkbox"
                                            label="Self"
                                            name="patientInfo-relationshipSelf"
                                            inputProps={{
                                                onChange: () =>
                                                    singleSelectCheckboxUtil(
                                                        form,
                                                        PATIENT_RELATIONSHIP_FIELDS,
                                                        'patientInfo-relationshipSelf',
                                                    ),
                                            }}
                                        />
                                        <MultistepFormField
                                            type="checkbox"
                                            label="Spouse"
                                            name="patientInfo-relationshipSpouse"
                                            inputProps={{
                                                onChange: () =>
                                                    singleSelectCheckboxUtil(
                                                        form,
                                                        PATIENT_RELATIONSHIP_FIELDS,
                                                        'patientInfo-relationshipSpouse',
                                                    ),
                                            }}
                                        />
                                        <MultistepFormField
                                            type="checkbox"
                                            label="Dependent Child"
                                            name="patientInfo-relationshipDependentChild"
                                            inputProps={{
                                                onChange: () =>
                                                    singleSelectCheckboxUtil(
                                                        form,
                                                        PATIENT_RELATIONSHIP_FIELDS,
                                                        'patientInfo-relationshipDependentChild',
                                                    ),
                                            }}
                                        />
                                        <MultistepFormField
                                            type="checkbox"
                                            label="Other"
                                            name="patientInfo-relationshipOther"
                                            inputProps={{
                                                onChange: () =>
                                                    singleSelectCheckboxUtil(
                                                        form,
                                                        PATIENT_RELATIONSHIP_FIELDS,
                                                        'patientInfo-relationshipOther',
                                                    ),
                                            }}
                                        />
                                    </Stack>
                                </Box>
                                <Box>
                                    <Typography>Student Status</Typography>
                                    <Stack>
                                        <MultistepFormField
                                            type="checkbox"
                                            label="FTS"
                                            name="patientInfo-studentStatusFTS"
                                            inputProps={{
                                                onChange: (e: any) => {
                                                    if (
                                                        form.getState().values[
                                                            'patientInfo-studentStatusPTS'
                                                        ]
                                                    ) {
                                                        singleSelectCheckboxUtil(
                                                            form,
                                                            ['patientInfo-studentStatusPTS'],
                                                            'patientInfo-studentStatusFTS',
                                                        )
                                                    } else {
                                                        form.change(
                                                            'patientInfo-studentStatusFTS',
                                                            e.target.checked,
                                                        )
                                                    }
                                                },
                                            }}
                                        />
                                        <MultistepFormField
                                            type="checkbox"
                                            label="PTS"
                                            name="patientInfo-studentStatusPTS"
                                            inputProps={{
                                                onChange: (e: any) => {
                                                    if (
                                                        form.getState().values[
                                                            'patientInfo-studentStatusFTS'
                                                        ]
                                                    ) {
                                                        singleSelectCheckboxUtil(
                                                            form,
                                                            ['patientInfo-studentStatusFTS'],
                                                            'patientInfo-studentStatusPTS',
                                                        )
                                                    } else {
                                                        form.change(
                                                            'patientInfo-studentStatusPTS',
                                                            e.target.checked,
                                                        )
                                                    }
                                                },
                                            }}
                                        />
                                    </Stack>
                                </Box>
                            </Stack>
                        </Grid>
                        <Grid item xs={8}>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <MultistepFormField
                                        label="Last Name"
                                        name="patientInfo-lastName"
                                        inputProps={{
                                            disabled: selfRelationshipEnabled,
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <MultistepFormField
                                        label="First Name"
                                        name="patientInfo-firstName"
                                        inputProps={{
                                            disabled: selfRelationshipEnabled,
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <MultistepFormField
                                        label="Middle Initial"
                                        name="patientInfo-middleInitial"
                                        inputProps={{
                                            disabled: selfRelationshipEnabled,
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <MultistepFormField
                                        label="Suffix"
                                        name="patientInfo-suffix"
                                        inputProps={{
                                            disabled: selfRelationshipEnabled,
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <MultistepFormField
                                        label="Address"
                                        name="patientInfo-address"
                                        inputProps={{
                                            disabled: selfRelationshipEnabled,
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <MultistepFormField
                                        label="State"
                                        name="patientInfo-state"
                                        inputProps={{
                                            disabled: selfRelationshipEnabled,
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <MultistepFormField
                                        label="City"
                                        name="patientInfo-city"
                                        inputProps={{
                                            disabled: selfRelationshipEnabled,
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <MultistepFormField
                                        label="Zip Code"
                                        name="patientInfo-zipCode"
                                        inputProps={{
                                            disabled: selfRelationshipEnabled,
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography>Gender</Typography>
                                    <Stack direction="row">
                                        <MultistepFormField
                                            type="checkbox"
                                            label="Male"
                                            name="patientInfo-genderMale"
                                            inputProps={{
                                                onChange: () =>
                                                    singleSelectCheckboxUtil(
                                                        form,
                                                        ['patientInfo-genderFemale'],
                                                        'patientInfo-genderMale',
                                                    ),
                                                disabled: selfRelationshipEnabled,
                                            }}
                                        />
                                        <MultistepFormField
                                            type="checkbox"
                                            label="Female"
                                            name="patientInfo-genderFemale"
                                            inputProps={{
                                                onChange: () =>
                                                    singleSelectCheckboxUtil(
                                                        form,
                                                        ['patientInfo-genderMale'],
                                                        'patientInfo-genderFemale',
                                                    ),
                                                disabled: selfRelationshipEnabled,
                                            }}
                                        />
                                    </Stack>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography>
                                        Patient ID / Account # (Assigned by Dentist)
                                    </Typography>
                                    <MultistepFormField name="patientInfo-patientId" />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                }
            />
        </Box>
    )
}

export default PatientInformationStep
